import React, {useState} from "react";
import strapi from "/scripts/apis/strapi/index.js";
import marked from "/scripts/marked/index.js";
import css from "./index.module.scss";
import DefaultLayout from "layouts/default";
import ImageCard from "@/components/imageCard";
import Button from "@/components/button";
import EnterpriseSvg from "/public/images/icons/enterprise.svg";
import Tag from "@/components/tag/index.jsx";
import { StrapiPicture } from "@/components/picture";
import { addToCart } from "@/scripts/utils";
import ProductCard from "@/components/productCard";
import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { useUser } from "@/scripts/apis/site/useUser";
import CopySvg from "/public/images/icons/copy.svg";
import gsap from "@/scripts/gsap";

export default function PurchasePage({featuredListings}) {
  const [purchase, setPurchase] = useState(null);
  const router = useRouter();
  const user = useUser();


  //only allow logged in users to access this page
  useEffect(() => {
    if (!user.loading && !user?.value?.id){
      router.push("/auth/signin");
    }
  }, [user]);


  useEffect(() => {
    if (router.query.id){
      strapi.query(strapi.endpoints.PURCHASE_ME_FIND_ONE, {
        data: {id: router.query.id},
        populate: {
          coverArt: true,
          listingOwner: {
            populate: {
              profilePicture: true,
            },
          },
          aiModel: true,
          tags: true,
          exampleImages: true,
        },
      }).then((res) => {
        setPurchase(res);
      });
    }
  }, [router.query.id]);

  const promptRef = useRef();
  const copiedRef = useRef();
  function copy(){
    navigator.clipboard.writeText(purchase?.prompt);

    let tl = gsap.timeline();
    tl.to(copiedRef.current, {opacity: 1, duration: 0.1});
    tl.to(promptRef.current, {opacity: 0, duration: 0.1}, "<");

    tl.to(copiedRef.current, {opacity: 0, duration: 0.1}, ">3");
    tl.to(promptRef.current, {opacity: 1, duration: 0.1}, "<");
  }

  return <DefaultLayout
    title={`${purchase?.name} | Lazy Prompts`}
    description={purchase?.shortDescription}
    mainClassName={css.main}
  >
    {purchase?.error && <>
      <section>
        <div className="sectionInner">
          <h2>Error</h2>
          <p>You do not have permission to view this page.</p>
          <Button label="Home" href="/"/>
        </div>
      </section>
    </>}
    {!purchase?.error && <>
      <section className={`light ${css.listing}`}>
        <div className="sectionInner">
          <ImageCard
            strapiPicture={purchase?.coverArt}
            overlaid
            light
            className={css.coverArt}
            sizes="(max-width: 1400px) 800px, 100vw"
          />
          <div className={`markdown ${css.about}`} dangerouslySetInnerHTML={{__html: marked(purchase?.description || "Lorem ipsum...")}}></div>
          <div className={css.title}>
            <div className={css.name}>{purchase?.business ? <EnterpriseSvg/> : undefined}{purchase?.name}</div>
            <a className={`custom ${css.author}`} href={`/stores/${purchase?.listingOwner?.id}`}>@{purchase?.listingOwner?.username}</a>
            <div className={`light ${css.tags}`}>
              {(purchase?.tags || []).map((tag) => <Tag key={tag.name} light label={tag.name}/>)}
            </div>
          </div>

          <div className={css.sidebar}>

            <div className={css.model}>
              <div className={css.label}>AI Model</div>
              <div
                className={"light markdown"}
                dangerouslySetInnerHTML={{
                  __html: marked(`This prompt is for <a href='${purchase?.aiModel?.site}' target='_blank'>${purchase?.aiModel?.name} ${purchase?.aiModel?.version}</a>. ${purchase?.aiModel?.information}\n\nAccess to ${purchase?.aiModel?.name} is not included with your purchase.`),
                }}>
              </div>
            </div>

            <div className={css.price}>${purchase?.price}</div>
          </div>
        </div>
      </section>
      <section className={`${css.prompt}`}>
        <div className="sectionInner">
          <div>
            <h2>Prompt</h2>
            <div className={css.copyable} onClick={copy}>
              <div className={css.icon}><CopySvg/></div>
              <pre ref={promptRef}>{purchase?.prompt}</pre>
              <div className={css.copied} ref={copiedRef}>Copied!</div>
            </div>
          </div>
          <div>
            <h2>How to Use</h2>
            <div className={css.guidelines} dangerouslySetInnerHTML={{__html: marked(purchase?.guidelines || "No guidelines included.")}}/>
          </div>
        </div>
      </section>

      <section className={`light ${css.examples}`}>
        <div className="sectionInner">
          <h2>Example Outputs</h2>
          {purchase?.exampleImages && <div className={css.exampleImages}>
            {(purchase?.exampleImages || []).map((example) => <ImageCard
              key={example.id}
              strapiPicture={example}
              overlaid
              light
              sizes="(max-width: 1300px) 50vw, (max-width:650px) 100vw, 25vw"
            />)}
          </div>}

          {purchase?.exampleText && <div className={css.exampleTexts}>
            {purchase?.exampleText.split("\n\n\n").map((example) => <div key={example}>{example.trim()}</div>)}
          </div>}
        </div>
      </section>
      <section className={`${css.author}`}>
        <div className="sectionInner">
          <h2>About the Engineer</h2>
          <a className={`custom ${css.username}`} href="/">
            <div className={css.profilePicture}>
              <StrapiPicture
                picture={purchase?.listingOwner?.profilePicture}
                alt = "Profile Picture"
                loading = "lazy"
                sizes = "200px"
                backgroundColor="var(--white)"
              />
            </div>
            <div className={css.text}>
              <div className={css.username}>@{purchase?.listingOwner?.username}</div>
              <div className={css.createdAt}>Joined on {(new Date(purchase?.listingOwner?.createdAt)).toLocaleDateString()}.</div>
            </div>
          </a>
          {purchase?.listingOwner?.biography && <div className={`markdown ${css.markdown}`} dangerouslySetInnerHTML={{
            __html: marked(purchase?.listingOwner?.biography),
          }}></div>}

        </div>
      </section>

      <section className={`light ${css.featured}`}>
        <div className="sectionInner">
          <h2>Featured Prompts</h2>
          <div className={css.prompts}>
            {featuredListings.map((featuredListing, idx) => <ProductCard
              light
              key={featuredListing.id}
              listing={featuredListing}
              sizes="(max-width: 1500px) 50vw, 500px"
              overlaid={idx === 0}
            />)}
          </div>
        </div>
      </section>
    </>}
  </DefaultLayout>;
}

export async function getStaticProps(query) {
  let featuredListings = await strapi.query(strapi.endpoints.LISTINGS_FIND, {
    data: {
      id: 17,
    },
    filters: {
      featured: true,
    },
    populate: {
      coverArt: true,
      aiModel: true,
    },
    pagination: {
      pageSize: 5,
    },
  });

  return {
    props: {
      featuredListings,
    },
    revalidate: 60,
  };
}
